import React, { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import { useI18n } from "../../contexts/I18nContext";
import { api, resolveUrl, formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/dateFormat";
import { Car, Users, Calendar, Settings, Plus, Trash2, Edit, Upload, X, ArrowUp, ArrowDown, Check, X as XIcon, BarChart3, Ticket, GripVertical, Star, CalendarPlus, CalendarMinus, FileText, ShieldAlert, Send, Ban, FileDown, Sheet, Link as LinkIcon, CreditCard, CalendarDays, Megaphone, FileWarning, Mail, ClipboardList, ClipboardCheck, GitCompare } from "lucide-react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import AdminAnalytics from "./AdminAnalytics";
import AdminCoupons from "./AdminCoupons";
import AdminReviews from "./AdminReviews";
import AdminBanners from "./AdminBanners";
import AdminCalendar from "./AdminCalendar";
import AdminDocuments from "./AdminDocuments";
import AdminLeads from "./AdminLeads";
import AdminNewsletter from "./AdminNewsletter";
import InspectionForm from "./InspectionForm";
import InspectionDiff from "./InspectionDiff";
import RichTextEditor from "../../components/RichTextEditor";
import BrandTab from "./settings/BrandTab";
import AppearanceTab from "./settings/AppearanceTab";
import SeoTab from "./settings/SeoTab";
import LoyaltyTab from "./settings/LoyaltyTab";
import LegalTab from "./settings/LegalTab";
import BillingTab from "./settings/BillingTab";
import CommsTab from "./settings/CommsTab";
import AboutTab from "./settings/AboutTab";

const adminGroupedLinks = (t) => [
  {
    key: "operations",
    label: "Operatii",
    icon: <BarChart3 className="w-3 h-3" />,
    links: [
      { to: "/admin", end: true, key: "dashboard", label: "Dashboard", icon: <BarChart3 className="w-4 h-4" /> },
      { to: "/admin/calendar", key: "calendar", label: "Calendar", icon: <CalendarDays className="w-4 h-4" /> },
      { to: "/admin/reservations", key: "reservations", label: t("admin.reservations"), icon: <Calendar className="w-4 h-4" /> },
    ],
  },
  {
    key: "fleet",
    label: "Flota",
    icon: <Car className="w-3 h-3" />,
    links: [
      { to: "/admin/cars", key: "cars", label: t("admin.cars"), icon: <Car className="w-4 h-4" /> },
      { to: "/admin/documents", key: "documents", label: "Documente", icon: <FileWarning className="w-4 h-4" /> },
      { to: "/admin/owners", key: "owners", label: t("admin.owners"), icon: <Users className="w-4 h-4" /> },
      { to: "/admin/reviews", key: "reviews", label: "Recenzii", icon: <Star className="w-4 h-4" /> },
    ],
  },
  {
    key: "marketing",
    label: "Marketing",
    icon: <Megaphone className="w-3 h-3" />,
    links: [
      { to: "/admin/leads", key: "leads", label: "Lead-uri", icon: <Mail className="w-4 h-4" /> },
      { to: "/admin/newsletter", key: "newsletter", label: "Newsletter", icon: <Send className="w-4 h-4" /> },
      { to: "/admin/coupons", key: "coupons", label: "Cupoane", icon: <Ticket className="w-4 h-4" /> },
      { to: "/admin/banners", key: "banners", label: "Bannere", icon: <Megaphone className="w-4 h-4" /> },
    ],
  },
  {
    key: "config",
    label: "Configurare",
    icon: <Settings className="w-3 h-3" />,
    links: [
      { to: "/admin/settings", key: "settings", label: t("admin.settings"), icon: <Settings className="w-4 h-4" /> },
    ],
  },
];

export default function AdminDashboard() {
  const { t } = useI18n();
  return (
    <DashboardLayout title={t("admin.dashboard")} groupedLinks={adminGroupedLinks(t)}>
      <Routes>
        <Route index element={<AdminAnalytics />} />
        <Route path="calendar" element={<AdminCalendar />} />
        <Route path="cars" element={<AdminCars />} />
        <Route path="owners" element={<AdminOwners />} />
        <Route path="reservations" element={<AdminReservations />} />
        <Route path="inspections/:reservationId/:type" element={<InspectionForm />} />
        <Route path="inspections/diff/:reservationId" element={<InspectionDiff />} />
        <Route path="leads" element={<AdminLeads />} />
        <Route path="newsletter" element={<AdminNewsletter />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="coupons" element={<AdminCoupons />} />
        <Route path="banners" element={<AdminBanners />} />
        <Route path="documents" element={<AdminDocuments />} />
        <Route path="settings" element={<AdminSettings />} />
      </Routes>
    </DashboardLayout>
  );
}

// ========== CARS ==========
function AdminCars() {
  const { t } = useI18n();
  const [cars, setCars] = useState([]);
  const [owners, setOwners] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    const r = await api.get("/admin/cars");
    setCars(r.data);
  };
  useEffect(() => {
    load();
    api.get("/admin/owners").then((r) => setOwners(r.data));
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Sterge masina si toate datele asociate?")) return;
    await api.delete(`/admin/cars/${id}`);
    await load();
  };

  const moveCar = async (id, direction) => {
    try {
      await api.post(`/admin/cars/${id}/move`, { direction });
      await load();
    } catch (e) {
      window.alert(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-cars">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8 sm:mb-10">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("admin.dashboard")}</div>
          <h1 className="font-serif text-3xl sm:text-4xl">{t("admin.cars")}</h1>
        </div>
        <button onClick={() => { setEditing(null); setShowForm(true); }} className="btn-gold-filled flex items-center gap-2" data-testid="add-car-btn">
          <Plus className="w-4 h-4" /> {t("admin.addCar")}
        </button>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
        {cars.map((c, idx) => {
          const owner = owners.find((o) => o.id === c.owner_id);
          const isFirst = idx === 0;
          const isLast = idx === cars.length - 1;
          return (
            <div key={c.id} className="card-surface p-6" data-testid={`admin-car-${c.id}`}>
              <div className="aspect-video overflow-hidden mb-4 bg-[var(--brand-surface-elevated)] relative">
                {c.images?.[0] ? (
                  <img src={resolveUrl(c.images[0].url)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: "var(--brand-text-secondary)" }}>No image</div>
                )}
                <div className="absolute top-2 left-2 flex flex-col gap-1">
                  <button
                    onClick={() => moveCar(c.id, "up")}
                    disabled={isFirst}
                    className="p-1.5 bg-black/70 hover:bg-[var(--brand-primary)] hover:text-black border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Muta in sus"
                    data-testid={`move-car-up-${c.id}`}
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => moveCar(c.id, "down")}
                    disabled={isLast}
                    className="p-1.5 bg-black/70 hover:bg-[var(--brand-primary)] hover:text-black border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    title="Muta in jos"
                    data-testid={`move-car-down-${c.id}`}
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="absolute top-2 right-2 text-xs px-2 py-1 bg-black/70 border border-white/10 font-mono" style={{ color: "var(--brand-primary)" }}>
                  #{idx + 1}
                </div>
              </div>
              <div className="label-eyebrow mb-1">{c.brand} &middot; {c.year}</div>
              <h3 className="font-serif text-xl mb-2">{c.name}</h3>
              <p className="text-xs mb-3" style={{ color: "var(--brand-text-secondary)" }}>
                €{c.price_eur}/zi &middot; {owner?.name || t("admin.noOwner")} &middot; {c.status}
              </p>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(c); setShowForm(true); }} className="btn-ghost flex-1 flex items-center justify-center gap-2" data-testid={`edit-car-${c.id}`}>
                  <Edit className="w-3.5 h-3.5" /> {t("common.edit")}
                </button>
                <button onClick={() => handleDelete(c.id)} className="btn-ghost" data-testid={`delete-car-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        })}
        {cars.length === 0 && (
          <div className="md:col-span-3 text-center py-16 text-sm" style={{ color: "var(--brand-text-secondary)" }}>
            Nu exista masini. Adauga prima.
          </div>
        )}
      </div>

      {showForm && (
        <CarFormDialog
          car={editing}
          owners={owners}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={async () => { await load(); setShowForm(false); setEditing(null); }}
        />
      )}
    </div>
  );
}

function CarFormDialog({ car, owners, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(
    car || {
      name: "", brand: "", model: "", year: 2024, transmission: "automatic", fuel: "petrol",
      seats: 5, price_eur: 100, weekend_price_eur: null, seasonal_prices: [], duration_discounts: [],
      description_ro: "", description_en: "", features: [], owner_id: "", status: "active",
    }
  );
  const [features, setFeatures] = useState((car?.features || []).join("\n"));
  const [images, setImages] = useState(car?.images || []);
  const [savedId, setSavedId] = useState(car?.id || null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [seasonalPrices, setSeasonalPrices] = useState(car?.seasonal_prices || []);
  const [durationDiscounts, setDurationDiscounts] = useState(car?.duration_discounts || []);

  const save = async () => {
    setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        seats: Number(form.seats),
        engine_cc: form.engine_cc ? Number(form.engine_cc) : null,
        horsepower: form.horsepower ? Number(form.horsepower) : null,
        price_eur: Number(form.price_eur),
        weekend_price_eur: form.weekend_price_eur ? Number(form.weekend_price_eur) : null,
        seasonal_prices: seasonalPrices.filter((s) => s.start_date && s.end_date && s.price_eur).map((s) => ({
          start_date: s.start_date, end_date: s.end_date, price_eur: Number(s.price_eur), label: s.label || "",
        })),
        duration_discounts: durationDiscounts.filter((d) => d.min_days && d.discount_pct).map((d) => ({
          min_days: Number(d.min_days), discount_pct: Number(d.discount_pct),
        })),
        features: features.split("\n").map((s) => s.trim()).filter(Boolean),
        owner_id: form.owner_id || null,
      };
      let saved;
      if (savedId) {
        saved = (await api.put(`/admin/cars/${savedId}`, payload)).data;
      } else {
        saved = (await api.post("/admin/cars", payload)).data;
        setSavedId(saved.id);
      }
      setImages(saved.images || []);
      setForm({...saved, weekend_price_eur: saved.weekend_price_eur});
      setSeasonalPrices(saved.seasonal_prices || []);
      setDurationDiscounts(saved.duration_discounts || []);
    } catch (e) { setError(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const uploadImages = async (files) => {
    if (!savedId) { setError("Salveaza intai masina"); return; }
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post(`/admin/cars/${savedId}/images`, fd);
      setImages((prev) => [...prev, r.data]);
    }
  };

  const deleteImage = async (imgId) => {
    await api.delete(`/admin/cars/${savedId}/images/${imgId}`);
    setImages((prev) => prev.filter((i) => i.id !== imgId));
  };

  const moveImage = async (idx, dir) => {
    const newImages = [...images];
    const target = idx + dir;
    if (target < 0 || target >= newImages.length) return;
    [newImages[idx], newImages[target]] = [newImages[target], newImages[idx]];
    setImages(newImages);
    await api.post(`/admin/cars/${savedId}/images/reorder`, { image_ids: newImages.map((i) => i.id) });
  };

  const [dragIdx, setDragIdx] = useState(null);
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = async (targetIdx) => {
    if (dragIdx === null || dragIdx === targetIdx) return;
    const newImages = [...images];
    const [moved] = newImages.splice(dragIdx, 1);
    newImages.splice(targetIdx, 0, moved);
    setImages(newImages);
    setDragIdx(null);
    await api.post(`/admin/cars/${savedId}/images/reorder`, { image_ids: newImages.map((i) => i.id) });
  };

  // Videos management
  const [videos, setVideos] = useState(car?.videos || []);
  const uploadVideo = async (file) => {
    if (!savedId || !file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/admin/cars/${savedId}/videos`, fd);
      setVideos((prev) => [...prev, r.data]);
    } catch (e) { setError(formatApiError(e)); }
  };
  const uploadVideoThumb = async (videoId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post(`/admin/cars/${savedId}/videos/${videoId}/thumbnail`, fd);
      setVideos((prev) => prev.map((v) => v.id === videoId ? r.data : v));
    } catch (e) { setError(formatApiError(e)); }
  };
  const deleteVideo = async (videoId) => {
    await api.delete(`/admin/cars/${savedId}/videos/${videoId}`);
    setVideos((prev) => prev.filter((v) => v.id !== videoId));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center overflow-auto p-3 sm:p-8" onClick={onClose} data-testid="car-form-dialog">
      <div className="card-surface w-full max-w-4xl p-5 sm:p-8 mt-4 sm:mt-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">{savedId ? t("common.edit") : t("admin.addCar")}</h2>
          <button onClick={onClose} data-testid="close-form"><X className="w-5 h-5" /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mb-6">
          <Input label="Nume" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="form-name" />
          <Input label="Marca" value={form.brand} onChange={(v) => setForm({ ...form, brand: v })} testid="form-brand" />
          <Input label="Model" value={form.model} onChange={(v) => setForm({ ...form, model: v })} testid="form-model" />
          <Input label="An" type="number" value={form.year} onChange={(v) => setForm({ ...form, year: v })} testid="form-year" />
          <Input label="Capacitate cilindrica (cc) - optional" type="number" value={form.engine_cc || ""} onChange={(v) => setForm({ ...form, engine_cc: v })} testid="form-engine-cc" />
          <Input label="Putere (CP) - optional" type="number" value={form.horsepower || ""} onChange={(v) => setForm({ ...form, horsepower: v })} testid="form-horsepower" />
          <Select label="Transmisie" value={form.transmission} onChange={(v) => setForm({ ...form, transmission: v })} options={[{v: "automatic", l: t("common.automatic")}, {v: "manual", l: t("common.manual")}]} testid="form-transmission" />
          <Select label="Combustibil" value={form.fuel} onChange={(v) => setForm({ ...form, fuel: v })} options={[{v: "petrol", l: t("common.petrol")}, {v: "diesel", l: t("common.diesel")}, {v: "hybrid", l: t("common.hybrid")}, {v: "electric", l: t("common.electric")}]} testid="form-fuel" />
          <Input label="Locuri" type="number" value={form.seats} onChange={(v) => setForm({ ...form, seats: v })} testid="form-seats" />
          <Input label="Pret EUR/zi (de baza)" type="number" value={form.price_eur} onChange={(v) => setForm({ ...form, price_eur: v })} testid="form-price" />
          <Input label="Pret weekend EUR/zi (optional)" type="number" value={form.weekend_price_eur || ""} onChange={(v) => setForm({ ...form, weekend_price_eur: v })} testid="form-weekend-price" />
          <Select label="Proprietar" value={form.owner_id || ""} onChange={(v) => setForm({ ...form, owner_id: v })} options={[{v: "", l: "(fara)"}, ...owners.map((o) => ({v: o.id, l: o.name}))]} testid="form-owner" />
          <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={[{v: "active", l: "Activ"}, {v: "inactive", l: "Inactiv"}]} testid="form-status" />
        </div>

        {/* Seasonal prices */}
        <div className="mb-4 card-surface-elevated p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="label-eyebrow">Preturi pe intervale (sezoniere)</label>
            <button type="button" onClick={() => setSeasonalPrices([...seasonalPrices, {start_date: "", end_date: "", price_eur: "", label: ""}])} className="btn-ghost text-xs" data-testid="add-seasonal">+ Adauga</button>
          </div>
          {seasonalPrices.length === 0 && <p className="text-xs" style={{color:"var(--brand-text-secondary)"}}>Niciun interval. Fara aceasta setare, se aplica pretul de baza/weekend.</p>}
          {seasonalPrices.map((s, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2 items-center" data-testid={`seasonal-${idx}`}>
              <input type="date" className="brand-input sm:col-span-3 text-xs" value={s.start_date} onChange={(e) => { const c=[...seasonalPrices]; c[idx]={...c[idx], start_date:e.target.value}; setSeasonalPrices(c); }} />
              <input type="date" className="brand-input sm:col-span-3 text-xs" value={s.end_date} onChange={(e) => { const c=[...seasonalPrices]; c[idx]={...c[idx], end_date:e.target.value}; setSeasonalPrices(c); }} />
              <input type="number" placeholder="€/zi" className="brand-input sm:col-span-2 text-xs" value={s.price_eur} onChange={(e) => { const c=[...seasonalPrices]; c[idx]={...c[idx], price_eur:e.target.value}; setSeasonalPrices(c); }} />
              <input placeholder="Eticheta (ex: Craciun)" className="brand-input sm:col-span-3 text-xs" value={s.label} onChange={(e) => { const c=[...seasonalPrices]; c[idx]={...c[idx], label:e.target.value}; setSeasonalPrices(c); }} />
              <button type="button" onClick={() => setSeasonalPrices(seasonalPrices.filter((_,i)=>i!==idx))} className="btn-ghost sm:col-span-1"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>

        {/* Duration discounts */}
        <div className="mb-4 card-surface-elevated p-4">
          <div className="flex items-center justify-between mb-3">
            <label className="label-eyebrow">Reduceri pentru durata</label>
            <button type="button" onClick={() => setDurationDiscounts([...durationDiscounts, {min_days: "", discount_pct: ""}])} className="btn-ghost text-xs" data-testid="add-discount">+ Adauga</button>
          </div>
          {durationDiscounts.length === 0 && <p className="text-xs" style={{color:"var(--brand-text-secondary)"}}>Ex: 7 zile → 10%, 14 zile → 15%. Se aplica cea mai mare reducere eligibila.</p>}
          {durationDiscounts.map((d, idx) => (
            <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 mb-2 items-center" data-testid={`discount-${idx}`}>
              <input type="number" placeholder="Min zile" className="brand-input sm:col-span-5 text-xs" value={d.min_days} onChange={(e) => { const c=[...durationDiscounts]; c[idx]={...c[idx], min_days:e.target.value}; setDurationDiscounts(c); }} />
              <input type="number" placeholder="% reducere" className="brand-input sm:col-span-5 text-xs" value={d.discount_pct} onChange={(e) => { const c=[...durationDiscounts]; c[idx]={...c[idx], discount_pct:e.target.value}; setDurationDiscounts(c); }} />
              <button type="button" onClick={() => setDurationDiscounts(durationDiscounts.filter((_,i)=>i!==idx))} className="btn-ghost sm:col-span-2"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
        </div>

        <div className="mb-4">
          <label className="label-eyebrow block mb-2">Descriere RO</label>
          <textarea rows={3} className="brand-input" value={form.description_ro} onChange={(e) => setForm({ ...form, description_ro: e.target.value })} data-testid="form-desc-ro" />
        </div>
        <div className="mb-4">
          <label className="label-eyebrow block mb-2">Descriere EN</label>
          <textarea rows={3} className="brand-input" value={form.description_en} onChange={(e) => setForm({ ...form, description_en: e.target.value })} data-testid="form-desc-en" />
        </div>
        <div className="mb-6">
          <label className="label-eyebrow block mb-2">Dotari (cate una pe linie)</label>
          <textarea rows={4} className="brand-input" value={features} onChange={(e) => setFeatures(e.target.value)} data-testid="form-features" />
        </div>

        {error && <div className="mb-4 text-sm" style={{ color: "var(--brand-primary)" }} data-testid="form-error">{error}</div>}

        <div className="flex gap-3 mb-8">
          <button onClick={save} disabled={busy} className="btn-gold-filled" data-testid="save-car-btn">
            {busy ? "..." : t("common.save")}
          </button>
          <button onClick={onClose} className="btn-ghost">{t("common.cancel")}</button>
        </div>

        {/* Images section */}
        {savedId && (
          <div className="border-t pt-6" style={{ borderColor: "var(--brand-border)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl">{t("admin.uploadImages")}</h3>
              <label className="btn-ghost cursor-pointer flex items-center gap-2">
                <Upload className="w-3.5 h-3.5" /> {t("admin.uploadImages")}
                <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => uploadImages(Array.from(e.target.files))} data-testid="upload-images-input" />
              </label>
            </div>
            <div className="grid grid-cols-4 gap-3">
              {images.map((img, idx) => (
                <div
                  key={img.id}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={handleDragOver}
                  onDrop={() => handleDrop(idx)}
                  className={`relative group aspect-square overflow-hidden border cursor-move ${dragIdx === idx ? "opacity-40" : ""}`}
                  style={{ borderColor: "var(--brand-border)" }}
                  data-testid={`image-${idx}`}
                >
                  <img src={resolveUrl(img.url)} alt="" className="w-full h-full object-cover pointer-events-none" />
                  <div className="absolute top-1 right-1 bg-black/60 p-1">
                    <GripVertical className="w-3 h-3 text-white" />
                  </div>
                  <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
                    <button onClick={() => moveImage(idx, -1)} className="p-1.5 bg-[var(--brand-surface)] hover:bg-[var(--brand-primary)] hover:text-black"><ArrowUp className="w-3 h-3" /></button>
                    <button onClick={() => moveImage(idx, 1)} className="p-1.5 bg-[var(--brand-surface)] hover:bg-[var(--brand-primary)] hover:text-black"><ArrowDown className="w-3 h-3" /></button>
                    <button onClick={() => deleteImage(img.id)} className="p-1.5 bg-[var(--brand-surface)] hover:bg-red-500"><Trash2 className="w-3 h-3" /></button>
                  </div>
                  {idx === 0 && <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 bg-[var(--brand-primary)] text-black">PRIMARY</span>}
                </div>
              ))}
            </div>

            {/* Videos */}
            <div className="mt-8 flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl">Video-uri</h3>
              <label className="btn-ghost cursor-pointer flex items-center gap-2">
                <Upload className="w-3.5 h-3.5" /> Incarca video (mp4/webm/mov)
                <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadVideo(e.target.files[0])} data-testid="upload-video-input" />
              </label>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {videos.map((v) => (
                <div key={v.id} className="card-surface-elevated p-3" data-testid={`video-${v.id}`}>
                  <div className="aspect-video bg-black mb-2 relative overflow-hidden">
                    {v.thumbnail_url ? (
                      <img src={resolveUrl(v.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <video src={resolveUrl(v.url)} className="w-full h-full object-cover" controls />
                    )}
                    {v.thumbnail_url && <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none"><span style={{color:"var(--brand-primary)",fontSize:"24px"}}>▶</span></div>}
                  </div>
                  <div className="flex gap-1">
                    <label className="btn-ghost flex-1 text-xs cursor-pointer flex items-center justify-center gap-1">
                      <Upload className="w-3 h-3" /> {v.thumbnail_url ? "Schimba thumb" : "Adauga thumb"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadVideoThumb(v.id, e.target.files[0])} data-testid={`video-thumb-${v.id}`} />
                    </label>
                    <button onClick={() => deleteVideo(v.id)} className="btn-ghost" data-testid={`delete-video-${v.id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {videos.length === 0 && <p className="col-span-3 text-xs" style={{color:"var(--brand-text-secondary)"}}>Niciun video. Format acceptat: mp4, webm, mov (max 200MB).</p>}
            </div>

            <CarDocumentsSection carId={savedId} />
          </div>
        )}
      </div>
    </div>
  );
}

function CarDocumentsSection({ carId }) {
  const [docs, setDocs] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    if (!carId) return;
    try {
      const [d, s] = await Promise.all([
        api.get(`/admin/cars/${carId}/documents`),
        api.get(`/admin/settings`),
      ]);
      setDocs(Array.isArray(d.data) ? d.data : []);
      setDocTypes(s.data?.document_types || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, [carId]);

  const remove = async (docId) => {
    if (!window.confirm("Sterge documentul?")) return;
    await api.delete(`/admin/cars/${carId}/documents/${docId}`);
    await load();
  };

  return (
    <div className="mt-8" data-testid="car-docs-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-serif text-lg">Documente expirare</h3>
        <button onClick={() => setEditing({})} className="btn-ghost flex items-center gap-2 text-xs" data-testid="add-car-doc"><Plus className="w-3.5 h-3.5" /> Document nou</button>
      </div>
      <p className="text-xs mb-4" style={{ color: "var(--brand-text-secondary)" }}>
        Alerte automate cu 14, 7 si 1 zi inainte de expirare catre admin si proprietar.
      </p>
      <div className="space-y-2">
        {docs.map((d) => {
          const meta = docTypes.find((x) => x.key === d.doc_type) || {};
          const label = d.custom_label_ro || meta.label_ro || d.doc_type;
          const daysUntil = Math.ceil((new Date(d.expires_at) - new Date()) / (1000 * 60 * 60 * 24));
          const isExpired = daysUntil < 0;
          const isCritical = daysUntil >= 0 && daysUntil <= 7;
          const color = isExpired ? "#fca5a5" : isCritical ? "#fde68a" : "var(--brand-text-secondary)";
          return (
            <div key={d.id} className="flex items-center gap-3 p-3 border" style={{ borderColor: "var(--brand-border)" }} data-testid={`car-doc-${d.id}`}>
              <div className="flex-1">
                <div className="font-medium text-sm">{label}</div>
                <div className="text-xs" style={{ color }}>
                  Expira: <b>{d.expires_at}</b> ({isExpired ? `acum ${-daysUntil} zile` : `in ${daysUntil} zile`})
                </div>
                {d.notes && <div className="text-xs mt-1" style={{ color: "var(--brand-text-secondary)" }}>{d.notes}</div>}
              </div>
              <button onClick={() => setEditing(d)} className="btn-ghost" data-testid={`edit-car-doc-${d.id}`}><Edit className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(d.id)} className="btn-ghost" data-testid={`del-car-doc-${d.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          );
        })}
        {docs.length === 0 && <p className="text-xs italic" style={{ color: "var(--brand-text-secondary)" }}>Niciun document inregistrat.</p>}
      </div>

      {editing && (
        <CarDocInlineModal
          carId={carId}
          doc={editing.id ? editing : null}
          docTypes={docTypes}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function CarDocInlineModal({ carId, doc, docTypes, onClose, onSaved }) {
  const [form, setForm] = useState({
    doc_type: doc?.doc_type || (docTypes[0]?.key || "rca"),
    expires_at: doc?.expires_at || "",
    notes: doc?.notes || "",
    custom_label_ro: doc?.custom_label_ro || "",
    custom_label_en: doc?.custom_label_en || "",
  });
  const [err, setErr] = useState("");
  const isCustom = form.doc_type === "__custom" || (doc?.doc_type && !docTypes.find((x) => x.key === doc.doc_type));

  const save = async () => {
    if (!form.expires_at) { setErr("Data expirare obligatorie"); return; }
    try {
      const payload = {
        doc_type: form.doc_type === "__custom" ? `custom_${Date.now()}` : form.doc_type,
        expires_at: form.expires_at,
        notes: form.notes,
        custom_label_ro: form.custom_label_ro,
        custom_label_en: form.custom_label_en,
      };
      if (doc?.id) await api.put(`/admin/cars/${carId}/documents/${doc.id}`, payload);
      else await api.post(`/admin/cars/${carId}/documents`, payload);
      onSaved();
    } catch (e) {
      setErr(formatApiError(e));
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div className="card-surface p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif text-lg">{doc?.id ? "Editeaza document" : "Document nou"}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="label-eyebrow block mb-2">Tip document</label>
            <select className="brand-input" value={form.doc_type} onChange={(e) => setForm({...form, doc_type: e.target.value})}>
              {docTypes.map((t) => <option key={t.key} value={t.key}>{t.label_ro}</option>)}
              <option value="__custom">+ Tip personalizat...</option>
            </select>
          </div>
          {isCustom && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label-eyebrow block mb-2">Label RO *</label>
                <input className="brand-input" value={form.custom_label_ro} onChange={(e) => setForm({...form, custom_label_ro: e.target.value})} placeholder="ex. Revizie" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">Label EN</label>
                <input className="brand-input" value={form.custom_label_en} onChange={(e) => setForm({...form, custom_label_en: e.target.value})} placeholder="e.g. Service" />
              </div>
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-2">Data expirare *</label>
            <input type="date" className="brand-input" value={form.expires_at} onChange={(e) => setForm({...form, expires_at: e.target.value})} data-testid="car-doc-expires" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Note</label>
            <textarea className="brand-input" rows={3} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} placeholder="Optional..." />
          </div>
        </div>
        {err && <div className="text-sm mt-3" style={{ color: "var(--brand-primary)" }}>{err}</div>}
        <div className="flex gap-3 mt-5">
          <button onClick={save} className="btn-gold-filled flex-1" data-testid="save-car-doc">Salveaza</button>
          <button onClick={onClose} className="btn-ghost">Anuleaza</button>
        </div>
      </div>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <input type={type} value={value} placeholder={placeholder || ""} onChange={(e) => onChange(e.target.value)} className="brand-input" data-testid={testid} />
    </div>
  );
}
function Textarea({ label, value, onChange, rows = 3, testid, placeholder }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <textarea rows={rows} value={value} placeholder={placeholder || ""} onChange={(e) => onChange(e.target.value)} className="brand-input resize-none" data-testid={testid} />
    </div>
  );
}

const DAYS_OF_WEEK = [
  { key: "Mo", label_ro: "Lu", label_en: "Mo" },
  { key: "Tu", label_ro: "Ma", label_en: "Tu" },
  { key: "We", label_ro: "Mi", label_en: "We" },
  { key: "Th", label_ro: "Jo", label_en: "Th" },
  { key: "Fr", label_ro: "Vi", label_en: "Fr" },
  { key: "Sa", label_ro: "Sa", label_en: "Sa" },
  { key: "Su", label_ro: "Du", label_en: "Su" },
];

function OpeningHoursEditor({ value = [], onChange }) {
  const slots = Array.isArray(value) ? value : [];
  const update = (i, patch) => {
    const next = slots.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    onChange(next);
  };
  const toggleDay = (i, dayKey) => {
    const cur = slots[i].days || [];
    const has = cur.includes(dayKey);
    update(i, { days: has ? cur.filter((d) => d !== dayKey) : [...cur, dayKey] });
  };
  const addSlot = () => onChange([...slots, { days: [], open: "09:00", close: "18:00" }]);
  const removeSlot = (i) => onChange(slots.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-3" data-testid="opening-hours-editor">
      {slots.map((slot, i) => (
        <div key={i} className="card-surface p-3 sm:p-4" data-testid={`opening-hours-slot-${i}`}>
          <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
            <div className="flex flex-wrap gap-1.5">
              {DAYS_OF_WEEK.map((d) => {
                const active = (slot.days || []).includes(d.key);
                return (
                  <button
                    type="button"
                    key={d.key}
                    onClick={() => toggleDay(i, d.key)}
                    className={`text-xs px-2 py-1 rounded transition ${active ? "border" : "opacity-60"}`}
                    style={{
                      background: active ? "var(--brand-primary)" : "transparent",
                      color: active ? "#050505" : "var(--brand-text)",
                      borderColor: "var(--brand-border)",
                      borderWidth: 1,
                    }}
                    data-testid={`oh-day-${i}-${d.key}`}
                  >
                    {d.label_ro}
                  </button>
                );
              })}
            </div>
            <button type="button" onClick={() => removeSlot(i)} className="btn-ghost p-1 text-xs" data-testid={`oh-remove-${i}`}>
              Sterge
            </button>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span>Deschis:</span>
            <input
              type="time"
              value={slot.open || ""}
              onChange={(e) => update(i, { open: e.target.value })}
              className="brand-input text-xs py-1"
              data-testid={`oh-open-${i}`}
            />
            <span>—</span>
            <span>Inchis:</span>
            <input
              type="time"
              value={slot.close || ""}
              onChange={(e) => update(i, { close: e.target.value })}
              className="brand-input text-xs py-1"
              data-testid={`oh-close-${i}`}
            />
          </div>
        </div>
      ))}
      <button type="button" onClick={addSlot} className="btn-ghost text-sm" data-testid="opening-hours-add">
        + Adauga interval
      </button>
    </div>
  );
}


function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="label-eyebrow block mb-2">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="brand-input" data-testid={testid}>
        {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
      </select>
    </div>
  );
}

// ========== OWNERS ==========
function AdminOwners() {
  const { t } = useI18n();
  const [owners, setOwners] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "" });
  const [error, setError] = useState("");

  const load = async () => setOwners((await api.get("/admin/owners")).data);
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: "", email: "", password: "", phone: "" });
    setError("");
    setShowForm(true);
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({ name: o.name, email: o.email, password: "", phone: o.phone || "" });
    setError("");
    setShowForm(true);
  };

  const save = async () => {
    setError("");
    try {
      if (editing) {
        const updates = { name: form.name, phone: form.phone };
        if (form.password) updates.password = form.password;
        await api.put(`/admin/owners/${editing.id}`, updates);
      } else {
        await api.post("/admin/owners", form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", email: "", password: "", phone: "" });
      await load();
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sterge proprietarul?")) return;
    try { await api.delete(`/admin/owners/${id}`); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-owners">
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("admin.dashboard")}</div>
          <h1 className="font-serif text-3xl sm:text-4xl">{t("admin.owners")}</h1>
        </div>
        <button onClick={openCreate} className="btn-gold-filled flex items-center gap-2" data-testid="add-owner-btn">
          <Plus className="w-4 h-4" /> {t("admin.addOwner")}
        </button>
      </div>

      <table className="w-full text-sm card-surface">
        <thead>
          <tr className="border-b" style={{ borderColor: "var(--brand-border)" }}>
            <th className="text-left p-4 label-eyebrow">Nume</th>
            <th className="text-left p-4 label-eyebrow">Email</th>
            <th className="text-left p-4 label-eyebrow">Telefon</th>
            <th className="text-right p-4 label-eyebrow">{t("common.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {owners.map((o) => (
            <tr key={o.id} className="border-b" style={{ borderColor: "var(--brand-border)" }} data-testid={`owner-${o.id}`}>
              <td className="p-4">{o.name}</td>
              <td className="p-4" style={{ color: "var(--brand-text-secondary)" }}>{o.email}</td>
              <td className="p-4" style={{ color: "var(--brand-text-secondary)" }}>{o.phone}</td>
              <td className="p-4 text-right flex justify-end gap-2">
                <button onClick={() => openEdit(o)} className="btn-ghost" data-testid={`edit-owner-${o.id}`}><Edit className="w-3.5 h-3.5" /></button>
                <button onClick={() => remove(o.id)} className="btn-ghost" data-testid={`delete-owner-${o.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-8" onClick={() => setShowForm(false)}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-xl sm:text-2xl mb-6">{editing ? `Editeaza ${editing.name}` : t("admin.addOwner")}</h2>
            <div className="space-y-4">
              <Input label="Nume" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="owner-name" />
              {!editing && <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="owner-email" />}
              {editing && <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>Email: {editing.email} (nu poate fi schimbat)</div>}
              <Input label="Telefon" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="owner-phone" />
              <Input label={editing ? "Parola noua (lasa gol pentru a pastra)" : "Parola"} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="owner-password" />
              {error && <div className="text-sm" style={{ color: "var(--brand-primary)" }}>{error}</div>}
              <div className="flex gap-3">
                <button onClick={save} className="btn-gold-filled flex-1" data-testid="save-owner-btn">{t("common.save")}</button>
                <button onClick={() => setShowForm(false)} className="btn-ghost">{t("common.cancel")}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== RESERVATIONS ==========
function AdminReservations() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [extendFor, setExtendFor] = useState(null);
  const [shortenFor, setShortenFor] = useState(null);
  const [newEndDate, setNewEndDate] = useState("");

  const load = async () => setItems((await api.get("/admin/reservations")).data);
  useEffect(() => { load(); }, []);

  const approve = async (id) => { await api.post(`/admin/reservations/${id}/approve`).catch((e) => alert(formatApiError(e))); await load(); };
  const reject = async (id) => { await api.post(`/admin/reservations/${id}/reject`).catch((e) => alert(formatApiError(e))); await load(); };
  const sendInvoice = async (id) => {
    try { const r = await api.post(`/admin/reservations/${id}/send-invoice`); alert(`Factura ${r.data.invoice_number} ${r.data.sent ? "trimisa" : "logata in consola"}`); }
    catch (e) { alert(formatApiError(e)); }
  };
  const sendCancellation = async (id) => {
    try { const r = await api.post(`/admin/reservations/${id}/send-cancellation-policy`); alert(r.data.sent ? "Politica trimisa" : "Logata in consola"); }
    catch (e) { alert(formatApiError(e)); }
  };
  const requestReview = async (id, targetType) => {
    try { const r = await api.post(`/admin/reservations/${id}/request-review?target_type=${targetType}`); alert(r.data.sent ? `Cerere recenzie (${targetType}) trimisa` : "Logata in consola"); }
    catch (e) { alert(formatApiError(e)); }
  };
  const submitExtend = async () => {
    try {
      await api.post(`/admin/reservations/${extendFor.id}/extend`, { new_end_date: newEndDate });
      setExtendFor(null); setNewEndDate("");
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };
  const cancel = async (id) => {
    if (!window.confirm("Anuleaza rezervarea?")) return;
    try { await api.post(`/admin/reservations/${id}/cancel`); await load(); }
    catch (e) { alert(formatApiError(e)); }
  };

  const downloadFile = async (path, filename) => {
    try {
      const r = await api.get(path, { responseType: "blob" });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(formatApiError(e)); }
  };

  const copyPayLink = async (id) => {
    const url = `${window.location.origin}/pay/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      alert(`Link plata copiat in clipboard:\n${url}`);
    } catch {
      window.prompt("Link plata:", url);
    }
  };
  const submitShorten = async () => {
    try {
      await api.post(`/admin/reservations/${shortenFor.id}/shorten`, { new_end_date: newEndDate });
      setShortenFor(null); setNewEndDate("");
      await load();
    } catch (e) { alert(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-reservations">
      <div className="mb-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("admin.dashboard")}</div>
          <h1 className="font-serif text-3xl sm:text-4xl">{t("admin.reservations")}</h1>
        </div>
        <button
          onClick={() => downloadFile("/admin/reservations/export.xlsx", `rezervari-${new Date().toISOString().slice(0,10)}.xlsx`)}
          className="btn-ghost flex items-center gap-2"
          data-testid="export-xlsx-btn"
        >
          <Sheet className="w-4 h-4" /> Export Excel
        </button>
      </div>
      <div className="space-y-4">
        {items.map((r) => (
          <div key={r.id} className="card-surface p-6" data-testid={`reservation-${r.id}`}>
            <div className="grid md:grid-cols-4 gap-4 text-sm mb-4">
              <div>
                <div className="label-eyebrow mb-1">Masina</div>
                <div>{r.car_name}</div>
                <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>Limba: {(r.lang || "ro").toUpperCase()}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Client</div>
                <div>{r.guest_name}</div>
                <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{r.guest_email}</div>
                <div className="text-xs" style={{ color: "var(--brand-text-secondary)" }}>{r.guest_phone}</div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Perioada</div>
                <div>{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</div>
                {r.extended_at && <div className="text-xs" style={{ color: "var(--brand-primary)" }}>prelungita</div>}
                {r.shortened_at && <div className="text-xs" style={{ color: "var(--brand-primary)" }}>scurtata</div>}
                {r.cancelled_at && <div className="text-xs" style={{ color: "#ef4444" }}>anulata</div>}
              </div>
              <div>
                <div className="label-eyebrow mb-1">{t("common.status")}</div>
                <div style={{ color: r.status === "approved" ? "var(--brand-primary)" : (r.status === "rejected" || r.status === "cancelled") ? "#ef4444" : "var(--brand-text-secondary)" }}>
                  {r.status}
                </div>
                {r.payment_status === "paid" && (
                  <div className="text-xs mt-1" style={{ color: "#22c55e" }} data-testid={`paid-badge-${r.id}`}>
                    Achitat online {r.payment_amount ? `(€${Number(r.payment_amount).toFixed(2)})` : ""}
                  </div>
                )}
              </div>
            </div>
            {r.custom_fields && Object.keys(r.custom_fields).length > 0 && (
              <div className="grid md:grid-cols-2 gap-3 text-xs pb-3 border-b mb-3" style={{ borderColor: "var(--brand-border)" }}>
                {Object.entries(r.custom_fields).map(([k, v]) => v && (
                  <div key={k}>
                    <span style={{ color: "var(--brand-text-secondary)" }}>{k}: </span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-wrap gap-2 pt-4 border-t" style={{ borderColor: "var(--brand-border)" }}>
              {r.status === "approved" && <>
                <button onClick={() => { setExtendFor(r); setNewEndDate(r.end_date); }} className="btn-ghost flex items-center gap-1.5" data-testid={`extend-${r.id}`}><CalendarPlus className="w-3.5 h-3.5" /> Prelungeste</button>
                <button onClick={() => { setShortenFor(r); setNewEndDate(r.end_date); }} className="btn-ghost flex items-center gap-1.5" data-testid={`shorten-${r.id}`}><CalendarMinus className="w-3.5 h-3.5" /> Scurteaza</button>
                {(new Date().toISOString().slice(0,10) < r.start_date) && (
                  <button onClick={() => cancel(r.id)} className="btn-ghost flex items-center gap-1.5" style={{borderColor:"#ef4444",color:"#ef4444"}} data-testid={`cancel-${r.id}`}><Ban className="w-3.5 h-3.5" /> Anuleaza</button>
                )}
                <button onClick={() => sendInvoice(r.id)} className="btn-ghost flex items-center gap-1.5" data-testid={`invoice-${r.id}`}><FileText className="w-3.5 h-3.5" /> Factura email</button>
                <button onClick={() => downloadFile(`/admin/reservations/${r.id}/invoice.pdf`, `factura-${r.id.slice(0,8)}.pdf`)} className="btn-ghost flex items-center gap-1.5" data-testid={`invoice-pdf-${r.id}`}><FileDown className="w-3.5 h-3.5" /> Factura PDF</button>
                <button onClick={() => downloadFile(`/admin/reservations/${r.id}/contract.pdf`, `contract-${r.id.slice(0,8)}.pdf`)} className="btn-ghost flex items-center gap-1.5" data-testid={`contract-pdf-${r.id}`}><FileDown className="w-3.5 h-3.5" /> Contract PDF</button>
                <button onClick={() => copyPayLink(r.id)} className="btn-ghost flex items-center gap-1.5" data-testid={`pay-link-${r.id}`}><CreditCard className="w-3.5 h-3.5" /> Link plata</button>
                <button onClick={() => sendCancellation(r.id)} className="btn-ghost flex items-center gap-1.5" data-testid={`cancel-policy-${r.id}`}><ShieldAlert className="w-3.5 h-3.5" /> Politica anulare</button>
                <button onClick={() => requestReview(r.id, "car")} className="btn-ghost flex items-center gap-1.5" data-testid={`request-review-${r.id}`}><Star className="w-3.5 h-3.5" /> Recenzie masina</button>
                <button onClick={() => requestReview(r.id, "site")} className="btn-ghost flex items-center gap-1.5" data-testid={`request-site-review-${r.id}`}><Star className="w-3.5 h-3.5" /> Recenzie site</button>
                <button onClick={() => navigate(`/admin/inspections/${r.id}/pickup`)} className="btn-ghost flex items-center gap-1.5" data-testid={`inspection-pickup-${r.id}`}><ClipboardList className="w-3.5 h-3.5" /> Inspectie predare</button>
                <button onClick={() => navigate(`/admin/inspections/${r.id}/return`)} className="btn-ghost flex items-center gap-1.5" data-testid={`inspection-return-${r.id}`}><ClipboardCheck className="w-3.5 h-3.5" /> Inspectie returnare</button>
                <button onClick={() => navigate(`/admin/inspections/diff/${r.id}`)} className="btn-ghost flex items-center gap-1.5" data-testid={`inspection-diff-${r.id}`}><GitCompare className="w-3.5 h-3.5" /> Raport diferente</button>
              </>}
              {(r.status === "in_progress" || r.status === "completed") && <>
                <button onClick={() => navigate(`/admin/inspections/${r.id}/pickup`)} className="btn-ghost flex items-center gap-1.5" data-testid={`inspection-pickup-${r.id}`}><ClipboardList className="w-3.5 h-3.5" /> Inspectie predare</button>
                <button onClick={() => navigate(`/admin/inspections/${r.id}/return`)} className="btn-ghost flex items-center gap-1.5" data-testid={`inspection-return-${r.id}`}><ClipboardCheck className="w-3.5 h-3.5" /> Inspectie returnare</button>
                <button onClick={() => navigate(`/admin/inspections/diff/${r.id}`)} className="btn-ghost flex items-center gap-1.5" data-testid={`inspection-diff-${r.id}`}><GitCompare className="w-3.5 h-3.5" /> Raport diferente</button>
              </>}
              {r.status === "pending" && (new Date().toISOString().slice(0,10) < r.start_date) && (
                <>
                  <button onClick={() => approve(r.id)} className="btn-gold-filled flex items-center gap-1.5" data-testid={`approve-${r.id}`}><Check className="w-3.5 h-3.5" /> {t("common.approve")}</button>
                  <button onClick={() => reject(r.id)} className="btn-ghost flex items-center gap-1.5" data-testid={`reject-${r.id}`}><XIcon className="w-3.5 h-3.5" /> {t("common.reject")}</button>
                </>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-center py-16 text-sm" style={{ color: "var(--brand-text-secondary)" }}>Nu exista rezervari</div>}
      </div>

      {extendFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-6" onClick={() => setExtendFor(null)}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-2">Prelungeste rezervarea</h2>
            <p className="text-sm mb-6" style={{ color: "var(--brand-text-secondary)" }}>{extendFor.car_name} &middot; client: {extendFor.guest_name}</p>
            <div className="mb-2 text-sm">Data curenta sfarsit: <span style={{ color: "var(--brand-primary)" }}>{fmtDate(extendFor.end_date)}</span></div>
            <label className="label-eyebrow block mb-2 mt-4">Noua data sfarsit</label>
            <input type="date" className="brand-input mb-6" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} min={extendFor.end_date} data-testid="extend-new-date" />
            <div className="flex gap-3">
              <button onClick={submitExtend} className="btn-gold-filled flex-1" data-testid="confirm-extend">Confirma</button>
              <button onClick={() => setExtendFor(null)} className="btn-ghost">Anuleaza</button>
            </div>
          </div>
        </div>
      )}
      {shortenFor && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3 sm:p-6" onClick={() => setShortenFor(null)}>
          <div className="card-surface p-5 sm:p-8 w-full max-w-md max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h2 className="font-serif text-2xl mb-2">Scurteaza rezervarea</h2>
            <p className="text-sm mb-6" style={{ color: "var(--brand-text-secondary)" }}>{shortenFor.car_name} &middot; client: {shortenFor.guest_name}</p>
            <div className="mb-2 text-sm">Data curenta sfarsit: <span style={{ color: "var(--brand-primary)" }}>{fmtDate(shortenFor.end_date)}</span></div>
            <label className="label-eyebrow block mb-2 mt-4">Noua data sfarsit (mai devreme)</label>
            <input type="date" className="brand-input mb-6" value={newEndDate} onChange={(e) => setNewEndDate(e.target.value)} min={shortenFor.start_date} max={shortenFor.end_date} data-testid="shorten-new-date" />
            <div className="flex gap-3">
              <button onClick={submitShorten} className="btn-gold-filled flex-1" data-testid="confirm-shorten">Confirma</button>
              <button onClick={() => setShortenFor(null)} className="btn-ghost">Anuleaza</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== SETTINGS ==========
function AdminSettings() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [savedMsg, setSavedMsg] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwMsg, setPwMsg] = useState("");

  // Tab state: persisted in URL (?tab=brand)
  const tabKeys = ["brand", "appearance", "seo", "comms", "billing", "legal", "loyalty", "about", "cont"];
  const params = new URLSearchParams(location.search);
  const urlTab = params.get("tab");
  const activeTab = tabKeys.includes(urlTab) ? urlTab : "brand";
  const setTab = (key) => {
    const p = new URLSearchParams(location.search);
    p.set("tab", key);
    navigate({ pathname: location.pathname, search: `?${p.toString()}` }, { replace: false });
  };

  const load = async () => setSettings((await api.get("/admin/settings")).data);
  useEffect(() => { load(); }, []);

  const flash = (m) => { setSavedMsg(m); setTimeout(() => setSavedMsg(""), 2500); };

  const saveBrand = async () => {
    await api.put("/admin/settings", {
      brand_name: settings.brand_name,
      tagline_ro: settings.tagline_ro,
      tagline_en: settings.tagline_en,
      colors: settings.colors,
      default_lang: settings.default_lang,
      default_currency: settings.default_currency,
      exchange_rate_eur_to_ron: parseFloat(settings.exchange_rate_eur_to_ron),
      contact: settings.contact,
      logo_only_mode: !!settings.logo_only_mode,
      show_owner_info: !!settings.show_owner_info,
    });
    flash("Salvat");
    window.location.reload();
  };

  const saveSmtp = async () => {
    await api.put("/admin/settings/smtp", settings.smtp);
    flash("SMTP salvat");
  };

  const saveSms = async () => {
    await api.put("/admin/settings/sms", settings.sms || {});
    flash("SMS salvat");
  };

  const savePayment = async () => {
    const p = { ...(settings.payment || {}) };
    if (p.deposit_pct !== undefined) p.deposit_pct = parseFloat(p.deposit_pct);
    await api.put("/admin/settings/payment", p);
    flash("Plata online salvata");
  };

  const saveHero = async () => {
    try {
      await api.put("/admin/settings", { hero: settings.hero });
      await load();
      flash("Hero salvat ✓");
    } catch (e) {
      flash("Eroare: " + formatApiError(e));
    }
  };

  const saveSeo = async () => {
    try {
      await api.put("/admin/settings", { seo: settings.seo });
      await load();
      flash("SEO salvat ✓");
    } catch (e) {
      flash("Eroare: " + formatApiError(e));
    }
  };

  const saveLoyalty = async () => {
    try {
      // Sanitize tiers: numeric + sorted
      const tiers = (settings.loyalty?.tiers || []).map(t => ({
        after_n: Number(t.after_n) || 0,
        discount_pct: Number(t.discount_pct) || 0,
        validity_days: Number(t.validity_days) || 180,
        label_ro: t.label_ro || "",
        label_en: t.label_en || "",
      })).filter(t => t.after_n > 0).sort((a,b) => a.after_n - b.after_n);
      await api.put("/admin/settings", { loyalty: { enabled: !!settings.loyalty?.enabled, tiers } });
      await load();
      flash("Loialitate salvat ✓");
    } catch (e) {
      flash("Eroare: " + formatApiError(e));
    }
  };

  const saveAbout = async () => {
    try {
      await api.put("/admin/settings", { about: settings.about });
      await load();
      flash("Pagina Despre salvata ✓");
    } catch (e) {
      flash("Eroare: " + formatApiError(e));
    }
  };

  const saveLegal = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const legal = { ...(settings.legal || {}) };
      // Update updated_at timestamps if content present
      if (legal.terms_ro || legal.terms_en) legal.terms_updated_at = today;
      if (legal.privacy_ro || legal.privacy_en) legal.privacy_updated_at = today;
      await api.put("/admin/settings", { legal });
      await load();
      flash("Legal salvat ✓");
    } catch (e) {
      flash("Eroare: " + formatApiError(e));
    }
  };

  const saveDisclosure = async () => {
    await api.put("/admin/settings", { disclosure: settings.disclosure || {} });
    flash("Politica afisare date salvata");
  };

  const saveFloating = async () => {
    await api.put("/admin/settings", { floating_contact: settings.floating_contact || {} });
    flash("Butoane flotante salvate");
  };

  const uploadHeroImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/settings/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setSettings({ ...settings, hero: { ...(settings.hero || {}), image_path: r.data.image_path } });
      flash("Imagine hero incarcata");
    } catch (e) { flash(formatApiError(e)); }
  };

  const uploadOgImage = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/admin/settings/seo-og-image", fd, { headers: { "Content-Type": "multipart/form-data" }});
      setSettings({ ...settings, seo: { ...(settings.seo || {}), og_image: r.data.og_image } });
      flash("Imagine OG incarcata");
    } catch (e) { flash(formatApiError(e)); }
  };

  const testSms = async () => {
    const to = window.prompt("Numar de telefon (ex. +40 700 000 000):");
    if (!to) return;
    try {
      const r = await api.post("/admin/settings/test-sms", { to, body: "Test SMS de la admin." });
      flash(r.data.sent ? "SMS trimis" : "SMS esuat / dezactivat (log in consola)");
    } catch (e) { flash(formatApiError(e)); }
  };

  const saveTemplates = async (lang) => {
    const tpl = lang === "en" ? settings.email_templates_en : settings.email_templates;
    await api.put("/admin/settings/email-templates", { templates: tpl, lang });
    flash(`Sabloane ${lang.toUpperCase()} salvate`);
  };

  const saveReservationFields = async () => {
    await api.put("/admin/settings", { reservation_fields: settings.reservation_fields });
    flash("Campuri rezervare salvate");
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    await api.post("/admin/settings/logo", fd);
    await load();
    flash("Logo incarcat");
  };
  const removeLogo = async () => { await api.delete("/admin/settings/logo"); await load(); };

  const testEmail = async () => {
    try {
      const r = await api.post("/admin/settings/test-email");
      if (r.data.sent) {
        flash(r.data.note || "Email trimis");
        return;
      }
      // Build detailed diagnostic report
      const d = r.data.diag || {};
      const ports = d.ports || {};
      const portsLines = Object.entries(ports)
        .map(([p, info]) => info.reachable ? `  ✓ Port ${p}: ACCESIBIL — ${info.banner}` : `  ✗ Port ${p}: BLOCAT — ${info.error}`)
        .join("\n");
      const lines = [
        "❌ SMTP test a esuat.",
        "",
        `📋 Eroare reala: ${r.data.error || "(necunoscuta)"}`,
        "",
        "🔍 Diagnostic conectivitate:",
        `  IP-ul server-ului nostru: ${d.server_ip || "?"}`,
        `  Host SMTP resolva la: ${d.host_resolves_to || "?"}`,
        portsLines || "  (fara teste de port)",
        "",
        `💡 Cauza probabila: ${r.data.note || ""}`,
      ];
      window.alert(lines.join("\n"));
      flash("Test SMTP esuat — vezi detalii in alerta.");
    } catch (e) {
      window.alert("Eroare test SMTP: " + (e?.response?.data?.detail || e.message));
    }
  };

  const runEmailJobs = async () => {
    const r = await api.post("/admin/settings/run-email-jobs");
    flash(`Reminder-uri: ${r.data.reminders_sent} &middot; Multumiri: ${r.data.thanks_sent}`);
  };

  if (!settings) return <div>...</div>;

  const changePassword = async () => {
    setPwMsg("");
    if (pwForm.new_password !== pwForm.confirm) { setPwMsg("Parolele nu coincid"); return; }
    if (pwForm.new_password.length < 6) { setPwMsg("Parola noua trebuie sa aiba minim 6 caractere"); return; }
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setPwForm({ current_password: "", new_password: "", confirm: "" });
      setPwMsg("Parola schimbata cu succes!");
    } catch (e) { setPwMsg(formatApiError(e)); }
  };

  const tabs = [
    { key: "brand", label: "Brand" },
    { key: "appearance", label: "Aparenta" },
    { key: "seo", label: "SEO & Tracking" },
    { key: "comms", label: "Comunicatii" },
    { key: "billing", label: "Plati & Rezervari" },
    { key: "legal", label: "Politici" },
    { key: "loyalty", label: "Loialitate" },
    { key: "about", label: "Pagina Despre" },
    { key: "cont", label: "Contul meu" },
  ];

  return (
    <div data-testid="admin-settings" className="space-y-6">
      <div>
        <div className="label-eyebrow mb-2" style={{ color: "var(--brand-primary)" }}>{t("admin.dashboard")}</div>
        <h1 className="font-serif text-3xl sm:text-4xl mb-6">{t("admin.settings")}</h1>
        {savedMsg && <div className="card-surface p-3 text-sm mb-4" style={{ color: "var(--brand-primary)" }} data-testid="settings-flash">{savedMsg}</div>}
      </div>

      {/* Tabs bar */}
      <div className="sticky top-0 z-20 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 pb-1 pt-1" style={{ background: "var(--brand-bg)" }}>
        <div className="flex gap-1 sm:gap-2 overflow-x-auto border-b" style={{ borderColor: "var(--brand-border)" }} data-testid="settings-tabs">
          {tabs.map((tb) => (
            <button
              key={tb.key}
              onClick={() => setTab(tb.key)}
              className={`px-3 sm:px-4 py-2.5 text-sm whitespace-nowrap transition border-b-2 -mb-px ${
                activeTab === tb.key
                  ? "border-[var(--brand-primary)] text-[var(--brand-primary)] font-medium"
                  : "border-transparent text-[var(--brand-text-secondary)] hover:text-[var(--brand-text)]"
              }`}
              data-testid={`settings-tab-${tb.key}`}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-8 pt-2">
      {/* ====== Tab content via extracted components ====== */}
      {activeTab === "brand" && (
        <BrandTab settings={settings} setSettings={setSettings} t={t} saveBrand={saveBrand} uploadLogo={uploadLogo} removeLogo={removeLogo} />
      )}
      {activeTab === "appearance" && (
        <AppearanceTab settings={settings} setSettings={setSettings} load={load} flash={flash} saveHero={saveHero} uploadHeroImage={uploadHeroImage} saveFloating={saveFloating} />
      )}
      {activeTab === "seo" && (
        <SeoTab settings={settings} setSettings={setSettings} saveSeo={saveSeo} uploadOgImage={uploadOgImage} />
      )}
      {activeTab === "loyalty" && (
        <LoyaltyTab settings={settings} setSettings={setSettings} saveLoyalty={saveLoyalty} />
      )}
      {activeTab === "legal" && (
        <LegalTab settings={settings} setSettings={setSettings} saveLegal={saveLegal} />
      )}
      {activeTab === "billing" && (
        <BillingTab settings={settings} setSettings={setSettings} saveDisclosure={saveDisclosure} savePayment={savePayment} saveReservationFields={saveReservationFields} />
      )}
      {activeTab === "comms" && (
        <CommsTab settings={settings} setSettings={setSettings} t={t} saveSmtp={saveSmtp} saveSms={saveSms} testSms={testSms} testEmail={testEmail} runEmailJobs={runEmailJobs} saveTemplates={saveTemplates} />
      )}
      {activeTab === "about" && (
        <AboutTab settings={settings} setSettings={setSettings} saveAbout={saveAbout} />
      )}
      {activeTab === "cont" && (
        <div className="max-w-md space-y-6" data-testid="settings-cont">
          <div>
            <div className="label-eyebrow mb-1" style={{ color: "var(--brand-primary)" }}>Contul meu</div>
            <h2 className="font-serif text-2xl mb-1">Schimba parola</h2>
            <p className="text-sm" style={{ color: "var(--brand-text-secondary)" }}>Parola trebuie sa aiba minim 6 caractere.</p>
          </div>
          {pwMsg && (
            <div className="text-sm p-3 rounded" style={{ background: pwMsg.includes("succes") ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", color: pwMsg.includes("succes") ? "#22c55e" : "#ef4444" }}>
              {pwMsg}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className="label-eyebrow block mb-1.5">Parola curenta</label>
              <input type="password" className="brand-input w-full" value={pwForm.current_password} onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })} data-testid="pw-current" autoComplete="current-password" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Parola noua</label>
              <input type="password" className="brand-input w-full" value={pwForm.new_password} onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })} data-testid="pw-new" autoComplete="new-password" />
            </div>
            <div>
              <label className="label-eyebrow block mb-1.5">Confirma parola noua</label>
              <input type="password" className="brand-input w-full" value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} data-testid="pw-confirm" autoComplete="new-password" onKeyDown={(e) => e.key === "Enter" && changePassword()} />
            </div>
            <button onClick={changePassword} className="btn-gold-filled w-full" data-testid="pw-submit">Schimba parola</button>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
